import { post } from '@/utils';
import { ApiResponse } from '@/utils';
import { getCurrentLanguage } from '@/i18n/config';
import {
  ChatGroupingParams,
  CreateChatParams,
  CreateChatResponse,
  ChatListParams,
  ChatListResponse,
  ChatFeedListParams,
  ChatFeedListResponse,
  ChatClearParams,
  DeleteChatParams,
  RenameChatParams,
  SendChatStreamParams,
  ChatInfoResponse,
} from '../types';
import { message as Message } from '@/components/common/Message';
import { beginAiStream } from '@/observability/aiStream';
import { getErrorCodeMessage, isRiskControlCode } from '@/utils/riskControl';
/**
 * AMAI聊天会话管理服务
 * 用于创建会话、获取会话列表、管理会话分组等
 */
class ChatService {
  // 维护仍需重命名(need_rename)的会话集合：只要未收到 rename 事件，就保持在集合中
  private pendingRenameChatIds: Set<string> = new Set<string>();

  constructor() {
    // 从本地存储恢复待重命名集合，增强刷新后的鲁棒性
    try {
      const raw = localStorage.getItem('amai_pending_rename_chat_ids');
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          this.pendingRenameChatIds = new Set<string>(arr.filter(Boolean));
        }
      }
    } catch (_) {
      // 静默处理
    }
  }

  private savePendingRenameToStorage(): void {
    try {
      localStorage.setItem(
        'amai_pending_rename_chat_ids',
        JSON.stringify(Array.from(this.pendingRenameChatIds))
      );
    } catch (_) {
      // 静默处理
    }
  }
  /**
   * 创建新会话
   * @param params 会话创建参数 { model_id_list, group_id?}
   * @returns 创建结果，返回会话ID
   */
  async createChat(params: CreateChatParams): Promise<ApiResponse<CreateChatResponse>> {
    return post<CreateChatResponse>('/chat', params);
  }

  /**
   * 获取会话列表
   * @param params 查询参数 { group_id?, page_number?, page_size?}
   * @returns 会话列表
   */
  async getChatList(params: ChatListParams): Promise<ApiResponse<ChatListResponse>> {
    return post<ChatListResponse>('/chat/list', params);
  }

  /**
   * 获取会话消息列表
   * @param params 查询参数 { chat_id}
   * @returns 会话消息列表
   */
  async getChatFeedList(params: ChatFeedListParams): Promise<ApiResponse<ChatFeedListResponse>> {
    return post<ChatFeedListResponse>('/chat/feed/list', params);
  }

  /**
   * 清空会话历史
   * @param params 参数 { chat_id}
   * @returns 操作结果
   */
  async clearChatHistory(params: ChatClearParams): Promise<ApiResponse<null>> {
    return post<null>('/chat/clear', params);
  }

  /**
   * 删除会话
   * @param params 参数 { chat_ids }
   * @returns 操作结果
   */
  async deleteChat(params: DeleteChatParams): Promise<ApiResponse<null>> {
    return post<null>('/chat/delete', params);
  }

  /**
   * 获取会话信息
   * @param params 参数 { chat_id }
   * @returns 会话信息，包含已引用文件数量 ref_count
   */
  async getChatInfo(params: { chat_id: string }): Promise<ApiResponse<ChatInfoResponse>> {
    return post<ChatInfoResponse>('/chat/info', params);
  }

  /**
   * 重命名会话
   * @param params 参数 { chat_id, name }
   * @returns 操作结果
   */
  async renameChat(params: RenameChatParams): Promise<ApiResponse<null>> {
    return post<null>('/chat/rename', params);
  }

  /**
   * 恢复会话流式输出
   * @param params 参数 { chat_id, msg_iid, event_id }
   * @param onMessage 消息回调函数
   * @param onError 错误回调函数
   * @param token 可选的认证token
   * @returns AbortController实例，用于取消请求
   */
  resumeChatStream(
    params: { chat_id: string; msg_iid: string; event_id: number },
    onMessage: (data: {
      event_type:
        | 'reasoning'
        | 'reply'
        | 'error'
        | 'system'
        | 'rename'
        | 'reasoning_end'
        | 'message_end';
      model_id: string;
      content: string;
      is_complete: boolean;
      need_rename?: boolean;
      duration?: number;
      msg_iid?: string;
    }) => void,
    onError: (error: any) => void,
    token?: string
  ): AbortController {
    const abortController = new AbortController();

    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let responseClone: Response | null = null;
    const completedModels = new Set<string>();

    // 准备headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'Accept-Language': getCurrentLanguage(),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      const storedToken = localStorage.getItem('anmo_ai_token');
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
    }

    // const apiPrefix = (import.meta as any).env.VITE_APP_BASE_API || '';
    // const url = new URL(`${apiPrefix}/chat/resume-stream`, (import.meta as any).env.VITE_APP_BASE_URL);
    // prod走实际，其他走本地代理
    const isProd = import.meta.env.MODE === 'prod';
    const apiPrefix = (import.meta as any).env.VITE_APP_BASE_API || '';
    let url: any = '';
    if(!isProd){
      url = `${apiPrefix}/chat/resume-stream`;
    }else{
      url = new URL(`${apiPrefix}/chat/resume-stream`, (import.meta as any).env.VITE_APP_BASE_URL);
    }
    console.log('[ChatService] Attempting to resume stream at URL:', url.toString());
    console.log('[ChatService] Resume stream params:', JSON.stringify(params));
    console.log('[ChatService] Resume stream headers:', JSON.stringify(headers));

    fetch(url.toString(), {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
      credentials: 'include',
      signal: abortController.signal,
    })
      .then(async response => {
        console.log(
          '[ChatService] Resume stream response received. Status:',
          response.status,
          'OK:',
          response.ok
        );
        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            '[ChatService] Resume stream connection error! Status:',
            response.status,
            'Response Text:',
            errorText
          );
          throw new Error(`HTTP error! status: ${response.status}. Details: ${errorText}`);
        }

        responseClone = response.clone();
        reader = response.body?.getReader() || null;
        if (!reader) {
          console.error('[ChatService] Resume stream response body is not readable or reader is null.');
          throw new Error('Response body is not readable');
        }
        console.log('[ChatService] Resume stream Reader obtained. Starting to read stream...');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log('[ChatService] Resume stream completed.');
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                console.log('[ChatService] Resume stream received [DONE] signal.');
                return;
              }

              try {
                const parsed = JSON.parse(data);
                // console.log('[ChatService] Resume stream received data:', parsed);

                if (parsed.event === 'reply') {
                    if (parsed.llm_id && parsed.content !== undefined) {
                      onMessage({
                        event_type: 'reply',
                        model_id: parsed.llm_id,
                        content: parsed.content,
                        is_complete: parsed.is_complete ?? false,
                      });
                      if (parsed.is_complete) {
                        completedModels.add(parsed.llm_id);
                      }
                    }
                  } else if (parsed.event === 'reasoning') {
                    if (parsed.llm_id && parsed.content !== undefined) {
                      onMessage({
                        event_type: 'reasoning',
                        model_id: parsed.llm_id,
                        content: parsed.content,
                        is_complete: parsed.is_complete ?? false,
                      });
                    }
                  } else if (parsed.event === 'error') {
                    console.error('[ChatService] Error event from SSE:', parsed);
                    onMessage({
                      event_type: 'error',
                      model_id: parsed.llm_id || 'unknown',
                      content: parsed.message || 'SSE Error',
                      is_complete: true,
                    });
                  } else if (parsed.event === 'reasoning_end') {
                    onMessage({
                      event_type: 'reasoning_end',
                      model_id: parsed.llm_id,
                      content: '',
                      duration: parsed.duration,
                      is_complete: false, // 思考结束不等于回复完成，模型还需要继续生成内容
                    });
                  } else if (parsed.event === 'message_end') {
                    console.log('[ChatService] MessageEnd event from SSE:', parsed);
                    const messageEndData = {
                      event_type: 'message_end' as const,
                      model_id: parsed.llm_id || 'unknown',
                      content: '',
                      is_complete: true,
                      msg_iid: parsed.msg_iid,
                    };
                    onMessage(messageEndData);
                  }
                // // 检查模型是否已完成
                // if (parsedData.is_complete && parsedData.model_id) {
                //   completedModels.add(parsedData.model_id);
                // }

                // onMessage(parsedData);
              } catch (e) {
                console.error('[ChatService] Error parsing SSE data line:', data, e);
              }
            }
          }
        }
      })
      .catch(error => {
        console.error('[ChatService] Resume stream error:', error);
        onError(error);
      })
      .finally(() => {
        if (reader) {
          reader.releaseLock();
        }
        if (responseClone) {
          responseClone.body?.cancel();
        }
      });

    return abortController;
  }

  /**
   * 调整会话所属分组
   * @param params 会话分组参数 {user_id, chat_id, old_group_id, new_group_id}
   * @returns 操作结果
   */
  async adjustGrouping(params: ChatGroupingParams): Promise<ApiResponse<null>> {
    return post<null>('/chat/grouping', params);
  }

  /**
   * 发送消息并获取流式响应
   * @param params 聊天参数 {chat_id, model_id_list, refs, message}
   * @param onMessage 接收消息的回调函数
   * @param onError 错误处理回调函数
   * @param token 可选的访问令牌
   * @returns AbortController实例，用于取消请求
   */
  sendChatStream(
    params: SendChatStreamParams,
    onMessage: (data: {
      event_type:
        | 'reasoning'
        | 'reply'
        | 'error'
        | 'system'
        | 'rename'
        | 'reasoning_end'
        | 'message_end';
      model_id: string;
      content: string;
      is_complete: boolean;
      need_rename?: boolean;
      duration?: number;
      msg_iid?: string;
    }) => void,
    onError: (error: any) => void,
    token?: string
  ): AbortController {
    const abortController = new AbortController();
    if (params.signal) {
      params.signal.addEventListener('abort', () => abortController.abort(), { once: true });
    }

    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null; // 在外部声明 reader
    let responseClone: Response | null = null; // 用于在中止时关闭连接
    const completedModels = new Set<string>(); // Track models that sent is_complete: true

    // 准备headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'Accept-Language': getCurrentLanguage(),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      const storedToken = localStorage.getItem('anmo_ai_token');
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
    }

    // prod走实际，其他走本地代理
    const isProd = import.meta.env.MODE === 'prod';
    const apiPrefix = (import.meta as any).env.VITE_APP_BASE_API || '';
    let url: any = '';
    if(!isProd){
      url = `${apiPrefix}/chat/stream`;
    }else{
      url = new URL(`${apiPrefix}/chat/stream`, (import.meta as any).env.VITE_APP_BASE_URL);
    }
    console.log('[ChatService] Attempting to connect to SSE URL:', url.toString());
    const { signal: _signal, ...restParams } = params;

    // 统一由服务层决定最终是否携带 need_rename：
    // 规则：如果调用方显式要求 need_rename 或者该会话仍处于“待重命名”集合中，则强制携带
    const chatId = restParams.chat_id as string;
    const carryFromPending = chatId ? this.pendingRenameChatIds.has(chatId) : false;
    const finalNeedRename = Boolean((restParams as any).need_rename || carryFromPending);
    const requestBody = { ...restParams, need_rename: finalNeedRename } as typeof restParams;

    // 如果仍需重命名，则将会话加入集合（或者保持不变）
    if (finalNeedRename && chatId) {
      this.pendingRenameChatIds.add(chatId);
      this.savePendingRenameToStorage();
    }

    console.log('[ChatService] Request params:', JSON.stringify(requestBody));
    console.log('[ChatService] Request headers:', JSON.stringify(headers));

    // ── AI 流式探针（P2-1）：观测首字延迟 / 断流 / 未正常收尾 / 连续失败 ──
    // 纯前端本地观测，不新增任何请求；探针异常不影响对话主流程。
    // model_id_list 可能有多个（多模型并行），多于一个时记为 'multi' 以保持维度低基数。
    const modelIdList = (restParams as { model_id_list?: unknown }).model_id_list;
    const modelTag =
      Array.isArray(modelIdList) && modelIdList.length > 0
        ? modelIdList.length === 1
          ? String(modelIdList[0])
          : 'multi'
        : undefined;
    const aiProbe = beginAiStream({ chatId, modelName: modelTag });

    fetch(url.toString(), {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
      credentials: 'include',
      signal: abortController.signal,
    })
      .then(async response => {
        console.log(
          '[ChatService] SSE fetch response received. Status:',
          response.status,
          'OK:',
          response.ok
        );
        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            '[ChatService] SSE connection error! Status:',
            response.status,
            'Response Text:',
            errorText
          );
          throw new Error(`HTTP error! status: ${response.status}. Details: ${errorText}`);
        }

        responseClone = response.clone(); // 克隆响应以便在中止时可以取消
        reader = response.body?.getReader() || null;
        if (!reader) {
          console.error('[ChatService] Response body is not readable or reader is null.');
          throw new Error('Response body is not readable');
        }
        console.log('[ChatService] SSE Reader obtained. Starting to read stream...');

        const decoder = new TextDecoder();
        let buffer = '';
        let hasReceivedData = false; // 标记是否收到过数据

        const processSSEStream = (chunk: Uint8Array) => {
          try {
            hasReceivedData = true; // 标记已收到数据
            // 探针：收到数据即喂看门狗（重置断流计时）
            aiProbe.onChunk();
            const decodedChunk = decoder.decode(chunk, { stream: true });
            // console.log('[ChatService] Decoded chunk:', decodedChunk); // Too verbose for general use
            const lines = decodedChunk.split('\n').filter(line => line.trim() !== '');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const dataContent = line.slice(6);
                if (dataContent === '[DONE]') {
                  console.log(
                    '[ChatService] SSE stream received [DONE]. This is an end-of-request signal.'
                  );
                  // 探针：正常收尾（幂等）
                  aiProbe.finish('done');
                  // No longer iterate and force complete all models here.
                  // Completion should be signaled by individual model replies with is_complete: true.
                  // completionSignaledViaDone = true; // REMOVED
                  return;
                }
                try {
                  const parsed = JSON.parse(dataContent);
                  // console.log('[ChatService] Parsed SSE data:', parsed); // Can be verbose

                  if (parsed.event === 'reply') {
                    if (parsed.llm_id && parsed.content !== undefined) {
                      // 探针：首个有效 token —— TTFT 的终点（幂等，只记第一次）
                      aiProbe.onFirstToken();
                      onMessage({
                        event_type: 'reply',
                        model_id: parsed.llm_id,
                        content: parsed.content,
                        is_complete: parsed.is_complete ?? false,
                      });
                      if (parsed.is_complete) {
                        completedModels.add(parsed.llm_id);
                      }
                    }
                  } else if (parsed.event === 'reasoning') {
                    if (parsed.llm_id && parsed.content !== undefined) {
                      onMessage({
                        event_type: 'reasoning',
                        model_id: parsed.llm_id,
                        content: parsed.content,
                        is_complete: parsed.is_complete ?? false,
                      });
                    }
                  } else if (parsed.event === 'error') {
                    console.error('[ChatService] Error event from SSE:', parsed);
                    onMessage({
                      event_type: 'error',
                      model_id: parsed.llm_id || 'unknown',
                      content: parsed.message || 'SSE Error',
                      is_complete: true,
                    });
                  } else if (parsed.event === 'rename') {
                    console.log('[ChatService] Rename event from SSE:', parsed);
                    onMessage({
                      event_type: 'rename',
                      model_id: parsed.llm_id || 'unknown',
                      content: parsed.content || '',
                      is_complete: true,
                    });
                    // 收到 rename 事件后，清除该会话的待重命名标记
                    if (chatId) {
                      this.pendingRenameChatIds.delete(chatId);
                      this.savePendingRenameToStorage();
                    }
                  } else if (parsed.event === 'reasoning_end') {
                    onMessage({
                      event_type: 'reasoning_end',
                      model_id: parsed.llm_id,
                      content: '',
                      duration: parsed.duration,
                      is_complete: false, // 思考结束不等于回复完成，模型还需要继续生成内容
                    });
                  } else if (parsed.event === 'message_end') {
                    console.log('[ChatService] MessageEnd event from SSE:', parsed);
                    const messageEndData = {
                      event_type: 'message_end' as const,
                      model_id: parsed.llm_id || 'unknown',
                      content: '',
                      is_complete: true,
                      msg_iid: parsed.msg_iid,
                    };
                    onMessage(messageEndData);
                  }
                } catch (e) {
                  console.error('[ChatService] Error parsing SSE data line:', dataContent, e);
                }
              }
            }
          } catch (error) {
            console.error('[ChatService] Error processing SSE stream chunk:', error);
          }
        };

        const readLoop = async () => {
          while (true) {
            const { done, value } = await reader!.read();
            if (done) {
              console.log('[ChatService] SSE stream reader reported done.');
              break;
            }
            // console.log('[ChatService] SSE chunk received, size:', value?.byteLength);

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // 保留最后一个不完整的行

            for (const line of lines) {
              processSSEStream(new TextEncoder().encode(line));
            }
          }
        };

        readLoop().catch(err => {
          if (!abortController.signal.aborted) {
            console.error('[ChatService] Error in SSE read loop:', err);
            onError(err);
          }
        });

        reader.closed
          .then(async () => {
            console.log('[ChatService] SSE stream reader reported done (reader.closed resolved).');

            // 如果没有收到任何数据，可能是服务器立即关闭了连接并在响应体中返回了错误
            if (!hasReceivedData) {
              console.log('[ChatService] No data received, checking response body for errors...');
              try {
                // 尝试读取响应体
                const responseText = await responseClone?.text();
                console.log('[ChatService] Response body:', responseText);

                if (responseText) {
                  try {
                    const errorData = JSON.parse(responseText);
                    console.log('[ChatService] Parsed response body:', errorData);
                    if (errorData.code === 600010) {
                      console.log(
                        '[ChatService] Detected credits insufficient error in response body'
                      );
                      // 通过 onError 回调传递特殊的错误对象
                      onError({
                        message: `Credits insufficient error. Details: ${responseText}`,
                        isCreditsError: true,
                        originalError: errorData,
                      });
                      return; // 不继续执行后续逻辑
                    }else if (isRiskControlCode(errorData.code)) {
                      // 风控类错误（敏感内容 / 服务繁忙 / 字数超限）：文案按错误码统一提示，
                      // 并回传 onError 让调用方（画布文本节点等）回到开始状态，便于用户修改后重发。
                      const riskMessage = getErrorCodeMessage(errorData.code);
                      if (riskMessage) Message.warning(riskMessage);
                      onError({
                        message: riskMessage,
                        isRiskControlError: true,
                        code: errorData.code,
                        originalError: errorData,
                      });
                      return; // 不继续执行后续逻辑
                    }
                    
                  } catch (parseError) {
                    console.log('[ChatService] Response body is not valid JSON:', parseError);
                  }
                }
              } catch (readError) {
                console.log('[ChatService] Failed to read response body:', readError);
              }
            }

            // Iterate through initially requested models. If any haven't sent a final is_complete=true signal via a 'reply' event,
            // send a completion signal for them now. This handles cases where a model's stream might end
            // without an explicit is_complete:true in its last chunk (e.g., server closes connection after last data).
            params.model_id_list.forEach(modelId => {
              if (!completedModels.has(modelId)) {
                console.log(
                  `[ChatService] Model ${modelId} did not explicitly signal completion. Marking complete due to stream closure.`
                );
                onMessage({
                  event_type: 'system',
                  model_id: modelId,
                  content: '',
                  is_complete: true,
                });
              }
            });
          })
          .catch(error => {
            if (!abortController.signal.aborted) {
              console.error('[ChatService] SSE stream reader.closed error:', error);
              onError(error);
            }
          })
          .finally(() => {
            console.log('[ChatService] Releasing reader lock.');
            reader?.releaseLock();
          });
      })
      .catch(error => {
        // 探针收尾（幂等）：AbortError = 用户主动停止，**不计入失败率**，
        // 与下方 onError 的判定口径保持一致，避免用户点「停止」污染指标。
        try {
          aiProbe.finish(error?.name === 'AbortError' ? 'abort' : 'error', {
            message: error?.message,
          });
        } catch {
          /* 埋点失败绝不影响对话流程 */
        }
        if (error.name !== 'AbortError') {
          console.error('[ChatService] Error in chat stream fetch/processing:', error);
          onError(error);
        }
      });

    // 扩展 AbortController 的 abort 方法以包含清理逻辑
    const originalAbort = abortController.abort.bind(abortController);
    abortController.abort = () => {
      console.log('[ChatService] Abort called. Cleaning up stream...');
      if (reader) {
        reader.cancel().catch(e => {
          if (e instanceof TypeError && e.message.includes('has been released')) {
            // 已经释放，忽略
          } else {
            console.error('[ChatService] Error cancelling reader on abort:', e);
          }
        });
        // 不需要再调用 releaseLock，cancel 已经释放了
        // reader.releaseLock();
        console.log('[ChatService] Reader cancelled.');
      }
      if (responseClone && responseClone.body) {
        // 尝试关闭响应体，这有助于释放资源
        responseClone.body
          .cancel()
          .catch(e => console.error('[ChatService] Error cancelling response body on abort:', e));
        console.log('[ChatService] Response body cancelled.');
      }
      originalAbort();
      console.log('[ChatService] Original abort executed.');
    };

    return abortController;
  }
}

export default new ChatService();
